import { useState } from "react";
import { Plus } from "lucide-react";
import { useLang } from "@/lib/adminLang";
import { nok, kompakt, fortegn, CATEGORIES, BEH_LABEL, STATUS_LABEL } from "./budsjettUtils";
import LineDrawer from "./LineDrawer";

export default function Costs({ data, scen, actions }) {
  const { t } = useLang();
  const { lines, computed, settings } = data;
  const costLines = lines.filter((l) => l.kind === "cost");
  const s = computed.scenarios[scen];
  const [drawer, setDrawer] = useState(null);
  const [collapsed, setCollapsed] = useState(() => new Set());

  const fcFor = (l) => (computed.lineForecasts[l.id] || {})[scen] || 0;
  const toggle = (k) =>
    setCollapsed((prev) => {
      const ny = new Set(prev);
      if (ny.has(k)) ny.delete(k);
      else ny.add(k);
      return ny;
    });

  return (
    <div data-testid="budget-costs" className="adm-rise">
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 mb-4">
        <p className="text-[11.5px] text-neutral-400 tabular-nums min-w-0">
          {t("Forecast")} {scen}: <b className="text-neutral-700">{kompakt(s.costs.total)}</b> {t("incl.")} {settings.contingencyPct}% {t("contingency")} · {s.physical.attendance} {t("in the room")} · {s.digital.sold} {t("digital")} · {s.dinner.guests} {t("dinner guests")}
        </p>
        <button type="button" onClick={() => setDrawer("new")} className="adm-btn adm-btn-sm" data-testid="cost-add-btn">
          <Plus size={12} /> {t("Add line")}
        </button>
      </div>

      {CATEGORIES.map((kat) => {
        const gruppe = costLines.filter((l) => l.category === kat.k);
        if (!gruppe.length) return null;
        const katSum = gruppe.reduce((a, l) => a + fcFor(l), 0);
        const andel = Math.round((katSum / (s.costs.subtotal || 1)) * 100);
        const lukket = collapsed.has(kat.k);
        return (
          <div key={kat.k} className="adm-gruppe" data-testid={`cost-cat-${kat.k}`}>
            <button
              type="button"
              onClick={() => toggle(kat.k)}
              className="w-full flex items-center justify-between px-4 py-4 text-left"
              data-testid={`cost-cat-toggle-${kat.k}`}
            >
              <span className="flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-neutral-500">
                <span className="bud-mono font-normal text-neutral-400 tracking-normal">{lukket ? "[+]" : "[−]"}</span>
                {t(kat.label)}
                <span className="text-neutral-400 normal-case tracking-normal font-normal">· {gruppe.length} {gruppe.length === 1 ? t("line") : t("lines")}</span>
              </span>
              <span className="bud-num text-[14px] tabular-nums">{nok(katSum)}</span>
            </button>
            <div className="px-4 pb-3 -mt-1 flex items-center gap-3" data-testid={`cost-cat-share-${kat.k}`}>
              <div className="adm-gruppe-bar flex-1"><span style={{ width: `${andel}%` }} /></div>
              <span className="bud-mono text-[9px] text-neutral-400 tabular-nums shrink-0">{andel}% {t("of costs")}</span>
            </div>
            {!lukket && (
              <div className="overflow-x-auto pb-2">
              <table className="adm-table adm-table-flat">
                <thead>
                  <tr>
                    <th>{t("Line")}</th>
                    <th>{t("Driver")}</th>
                    <th className="adm-right">{t("Budget")}</th>
                    <th className="adm-right">{t("Committed")}</th>
                    <th className="adm-right">{t("Forecast")}</th>
                    <th className="adm-right">{t("Actual")}</th>
                    <th className="adm-right">Δ</th>
                    <th>{t("Status")}</th>
                  </tr>
                </thead>
                <tbody>
                  {gruppe.map((l) => {
                    const fc = fcFor(l);
                    const diff = fc - (l.budget || 0);
                    const overridden = (l.scenarioOverrides || {})[scen] != null;
                    return (
                      <tr key={l.id} onClick={() => setDrawer(l)} className="cursor-pointer" data-testid={`cost-row-${l.id}`}>
                        <td>
                          <span className="font-medium">{l.name}</span>
                          {l.supplier && <span className="text-[11px] text-neutral-400 ml-2">{l.supplier}</span>}
                        </td>
                        <td className="text-[11px] text-neutral-400 whitespace-nowrap">
                          {t(BEH_LABEL[l.behavior] || "Fixed")}
                          {["per_physical", "per_digital", "per_dinner"].includes(l.behavior) && ` · ${nok(l.unitCost)}${t("/head")}`}
                          {l.behavior === "stepped" && ` · ${nok(l.unitCost)}/${l.unitSize}`}
                          {l.behavior === "pct_revenue" && ` · ${l.unitCost}%`}
                        </td>
                        <td className="text-right tabular-nums text-neutral-500">{nok(l.budget)}</td>
                        <td className="text-right tabular-nums text-neutral-500">{l.committed ? nok(l.committed) : "—"}</td>
                        <td className="text-right tabular-nums font-semibold">
                          {nok(fc)}
                          {overridden && <span className="text-amber-600" title={t("Overridden in this scenario")}> *</span>}
                        </td>
                        <td className="text-right tabular-nums text-neutral-500">{l.actual ? nok(l.actual) : "—"}</td>
                        <td className={`text-right tabular-nums text-[12px] font-medium whitespace-nowrap ${diff > 0 ? "text-red-600" : diff < 0 ? "text-emerald-700" : "text-neutral-300"}`}>
                          {diff === 0 ? "—" : `${fortegn(diff)} ${diff > 0 ? "↑" : "↓"}`}
                        </td>
                        <td className="text-[11px] text-neutral-500 whitespace-nowrap">
                          {t(STATUS_LABEL[l.status] || l.status)}
                          {l.quoteAmount != null && (
                            <span className="block text-[10px] text-neutral-400 tabular-nums">{t("Quote")} {nok(l.quoteAmount)}</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              </div>
            )}
          </div>
        );
      })}

      <div className="adm-gruppe px-4 pt-2.5 mt-8" data-testid="cost-totals">
        <div className="flex items-center justify-between text-[13px] py-1">
          <span className="text-neutral-500">{t("Subtotal")}</span>
          <span className="tabular-nums font-medium">{nok(s.costs.subtotal)}</span>
        </div>
        <div className="flex items-center justify-between text-[13px] py-1 border-t border-[#F1EEE7]">
          <span className="text-neutral-500">
            {t("Contingency")} ({settings.contingencyPct}% {t("of")} {(settings.contingencyCategories || []).map((c) => t(CATEGORIES.find((k) => k.k === c)?.label || c)).join(", ")})
          </span>
          <span className="tabular-nums font-medium">{nok(s.costs.contingency)}</span>
        </div>
        <div className="flex items-center justify-between text-[14px] py-2 border-t border-neutral-900 mt-1">
          <span className="font-semibold">{t("Forecast costs")} · {scen}</span>
          <span className="tabular-nums font-semibold">{nok(s.costs.total)}</span>
        </div>
      </div>

      {drawer && (
        <LineDrawer
          line={drawer === "new" ? null : drawer}
          kind="cost"
          members={data.members}
          onSave={async (payload) => {
            if (drawer === "new") await actions.newLine({ ...payload, kind: "cost" });
            else await actions.patchLine(drawer.id, payload);
            setDrawer(null);
          }}
          onDelete={
            drawer !== "new"
              ? async () => {
                  if (window.confirm(t("Delete this line?"))) {
                    await actions.deleteLine(drawer.id);
                    setDrawer(null);
                  }
                }
              : null
          }
          onClose={() => setDrawer(null)}
        />
      )}
    </div>
  );
}
